import { T } from "@angular/cdk/keycodes";
import { AfterViewInit, Component, Input, Output, EventEmitter, TemplateRef, ContentChild, QueryList, AfterContentInit, ContentChildren } from "@angular/core";
import { ShiftsService } from "ng/src/app/shifts/shifts.service";
import { LogService } from "ng/src/app/logs/log.service";
import { ScheduledShift, Shift, ShiftRole } from "ng/src/app/shifts/shift.model";
import { Utils } from "ng/src/app/core/utils";
import { PrimeTemplate } from "primeng/api";
import { BehaviorSubject, forkJoin, Observable, of, pipe } from "rxjs";
import { NoteService } from "ng/src/app/notes/shared/note.service";
import { Note } from "ng/src/app/notes/shared/note.model";
import { filter, finalize, map, switchMap, tap } from "rxjs/operators";

@Component({
	selector: 'shift-sign-on',
	templateUrl: './shift-sign-on.component.html',
	styleUrls: ['shift-sign-on.component.scss']
})
export class ShiftSignOnComponent implements AfterViewInit {
	
	@Output('signOnCompleted') complete = new EventEmitter<Shift | undefined>();
	@ContentChildren(PrimeTemplate) templates?: QueryList<PrimeTemplate>;
	
	constructor(
		private shiftService: ShiftsService,
		private noteService: NoteService
	) {}
	
	private _ackQuery?: Note.Query
	private _selectedShift$ = new BehaviorSubject<ScheduledShift | undefined>(undefined);
	private _selectedRole$ = new BehaviorSubject<ShiftRole | undefined>(undefined);
	private _signOnCompleted$ = new BehaviorSubject<Shift | undefined>(undefined);
	
	buttonTemplate?: TemplateRef<any>;
	
	shiftStart = new Date();
	shiftEnd = new Date();

	loadingShifts = false;
	signOnPending = false;
	ackNotes: Note[] = [];
	roles: ShiftRole[] = [];
	scheduledShifts: ScheduledShift[] = [];
	
	get signOnCompleted() {
		return this._signOnCompleted$.getValue();
	}
	
	set signOnCompleted(val: Shift | undefined) {
		this._signOnCompleted$.next(val);
	}
	
	get signOnCompleted$() {
		return this._signOnCompleted$.asObservable();
	}
	
	set selectedShift(val: ScheduledShift | undefined) {
		this._selectedShift$.next(val);
	}
	
	get selectedShift() {
		return this._selectedShift$.getValue();
	}
	
	set selectedRole(val: ShiftRole | undefined) {
		this._selectedRole$.next(val);
	}
	
	get selectedRole() {
		return this._selectedRole$.getValue();
	}
	
	get signOnEnabled() {
		if (!this.shiftStart || !this.shiftEnd) return false;
		return this.shiftStart < this.shiftEnd;
	}
	
	get isMobile() {
		return Utils.isMobile;
	}
	
	ngAfterViewInit(): void {
		if (this.templates) {
			this.buttonTemplate = this.templates?.find(t => t.name === 'button')?.template;
		}
		
		this.reset();
		this._selectedShift$.subscribe((shift) => {
			if (!shift) return;
			const dates = this.shiftService.getShiftDates(shift);
			this.shiftStart = dates[0];
			this.shiftEnd = dates[1];
		})
	}
	
	reset() {
		delete this._ackQuery;
		this.signOnPending = false;
		this.loadingShifts = true;
		const now = new Date();
		now.setSeconds(0, 0);
		
		forkJoin({
			shifts: this.shiftService.getScheduledShifts(),
			roles: this.shiftService.getShiftRoles()
		})
			.pipe(
				finalize(() => this.loadingShifts = false)
			)
			.subscribe(res => {
				this.roles = res.roles;
				this.selectedRole = this.roles[0];
				
				this.scheduledShifts = res.shifts;
				this.selectedShift = this.shiftService.findBestShift(this.scheduledShifts);
			});
		
		this.shiftStart = new Date();
		this.shiftEnd = new Date(now.getTime() + 60*60*1000);
		this.ackNotes.length = 0;
		this._selectedShift$.next(undefined);
		this._selectedRole$.next(undefined);
		this.signOnCompleted = undefined;
	}
	
	presentAcksForRole() {
		this.signOnPending = true;
		this._ackQuery = {
			log: LogService.selectedLog?.id,
			sort: ['noteTime,asc', 'updated,asc']
		}
		
		if (this.selectedRole)
		{
			this._ackQuery.mustAckForShiftSignIn = this.selectedRole.id;
		}
		
		this.signOn();
	}
	
	signOn() {
		this.signOnPending = true;
		this.shiftStart.setSeconds(0, 0);
		this.shiftEnd.setSeconds(0, 0);
		
		this.updateAckNotesForQuery()
			.pipe(switchMap(notes => {
				if (notes.length) return of(undefined);
				
				return this.shiftService.signOn({
						startTime: this.shiftStart,
						endTime: this.shiftEnd,
						role: this.selectedRole
					})
					.pipe(
						switchMap((shift) => {
							return this.shiftService.refreshActiveShifts()
								.pipe(map(() => shift));
						}),
						finalize(() => {
							this.signOnPending = false;
						})
					)
			}),
				filter((s) => !!s)
			)
			.subscribe((newShift) => {
				this.complete.next(newShift);
				this._signOnCompleted$.next(newShift);
			})
	}
	
	private updateAckNotesForQuery(): Observable<Note[]> {
		return (
			(!this._ackQuery || !this._ackQuery.mustAckForShiftSignIn)
				? of([] as Note[])
				: this.noteService.getLogNotes(this._ackQuery)
		)
			.pipe(
				tap((notes) => this.ackNotes = notes)
			)
	}
	
}